/**
 * A ready-to-send email (14 #9 "Request info", 15 #9 "Chase"). Two ways out, both recorded in each drawing's
 * history by the server:
 *  - "Send email" from Sara's mailbox — only when Outgoing email (`email_outbound`) is set up and switched on;
 *  - otherwise (or by choice) "Copy text" / "Open in mail app", then "Log as sent".
 * The status of Outgoing email is checked only when the composer opens (one request per session).
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Copy, Loader2, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NotConfigured } from '@/components/ui/not-configured';
import { notify } from '@/components/ui/notify';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { getErrorMessage } from '@/lib/errors';
import { isFeatureNotEnabledError } from '@/lib/integrations';
import { cn } from '@/lib/utils';
import { mailtoHref } from './model';

const EMAIL_RE = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;
const fieldClass = 'h-9 w-full rounded-control border border-line bg-surface px-2.5 text-[13px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]';

/** 'a@x.com; b@y.com' → ['a@x.com', 'b@y.com'] and the parts that are not addresses. */
export function splitAddresses(value) {
  const parts = String(value || '').split(/[,;\s]+/).map((part) => part.trim()).filter(Boolean);
  return { valid: parts.filter((part) => EMAIL_RE.test(part)), invalid: parts.filter((part) => !EMAIL_RE.test(part)) };
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const area = document.createElement('textarea');
  area.value = text;
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  area.remove();
  return undefined;
}

function ComposerBody({ initial, recent = [], summary, onSubmit, onDone, sendHint, testId }) {
  const email = useFeature('email_outbound');
  const listId = useId();
  const [to, setTo] = useState(initial.to || '');
  const [cc, setCc] = useState(initial.cc || '');
  const [subject, setSubject] = useState(initial.subject || '');
  const [body, setBody] = useState(initial.body || '');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [opened, setOpened] = useState(false);
  useEffect(() => { setTo(initial.to || ''); setCc(initial.cc || ''); }, [initial.to, initial.cc]);

  const toParts = useMemo(() => splitAddresses(to), [to]);
  const ccParts = useMemo(() => splitAddresses(cc), [cc]);
  const badAddress = toParts.invalid[0] || ccParts.invalid[0] || '';
  const href = mailtoHref({ to: toParts.valid.join(','), cc: ccParts.valid.join(','), subject, body });
  const canSend = email.enabled && toParts.valid.length > 0 && !badAddress && subject.trim() && body.trim();

  const submit = async (send) => {
    if (badAddress) { setError(`“${badAddress}” is not an email address`); return; }
    if (!subject.trim() || !body.trim()) { setError('Add a subject and a message'); return; }
    if (send && !toParts.valid.length) { setError('Add the email address to send to'); return; }
    setBusy(send ? 'send' : 'log');
    setError('');
    try {
      await onSubmit({ to: toParts.valid.join(', '), cc: ccParts.valid.join(', '), subject: subject.trim(), body: body.trim(), send });
      onDone();
    } catch (err) {
      if (isFeatureNotEnabledError(err)) email.refresh?.();
      setError(getErrorMessage(err, send ? 'Could not send the email' : 'Could not log it'));
    } finally {
      setBusy('');
    }
  };

  const copy = async () => {
    try {
      await copyToClipboard(`${subject}\n\n${body}`);
      notify.success('Email text copied', { description: 'Paste it into your mail app, then come back and log it.' });
    } catch {
      notify.error('Could not copy the text');
    }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {summary ? <div className="rounded-xl border border-line-2 bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2">{summary}</div> : null}
      <datalist id={listId}>{recent.map((address) => <option key={address} value={address} />)}</datalist>
      <div className="grid gap-2 sm:grid-cols-[64px_1fr] sm:items-center">
        <label htmlFor={`${listId}-to`} className="text-[12px] font-semibold text-ink-2">To</label>
        <input id={`${listId}-to`} list={listId} value={to} onChange={(event) => setTo(event.target.value)} placeholder="name@company.com" className={fieldClass} data-testid={`${testId}-to`} autoComplete="off" />
        <label htmlFor={`${listId}-cc`} className="text-[12px] font-semibold text-ink-2">Cc</label>
        <input id={`${listId}-cc`} list={listId} value={cc} onChange={(event) => setCc(event.target.value)} placeholder="optional" className={fieldClass} data-testid={`${testId}-cc`} autoComplete="off" />
        <label htmlFor={`${listId}-subject`} className="text-[12px] font-semibold text-ink-2">Subject</label>
        <input id={`${listId}-subject`} value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={200} className={fieldClass} data-testid={`${testId}-subject`} />
      </div>
      <label className="block">
        <span className="sr-only">Message</span>
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          maxLength={10000}
          rows={12}
          className="min-h-[220px] w-full resize-y rounded-control border border-line bg-surface px-3 py-2.5 font-mono text-[12.5px] leading-[19px] text-ink outline-none focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
          data-testid={`${testId}-body`}
        />
      </label>

      {email.enabled ? (
        <p className="text-[12px] text-ink-3" data-testid={`${testId}-send-note`}>{sendHint || 'Sends from Sara’s mailbox; replies come back to you.'} Each drawing’s history records it — a sent email cannot be undone.</p>
      ) : (
        <NotConfigured
          feature="email_outbound"
          layout="inline"
          title="Sending from Sara needs Outgoing email"
          text="Copy the text or open it in your mail app, send it from there, then press Log as sent — each drawing’s history records it."
          testId={`${testId}-email-off`}
        />
      )}
      {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line-2 pt-3">
        <Button type="button" variant="ghost" size="sm" onClick={copy} disabled={Boolean(busy)} data-testid={`${testId}-copy`}>
          <Copy aria-hidden="true" /> Copy text
        </Button>
        {href ? (
          <Button asChild variant="outline" size="sm">
            <a href={href} onClick={() => setOpened(true)} data-testid={`${testId}-mailto`}>
              <Mail aria-hidden="true" /> Open in mail app
            </a>
          </Button>
        ) : (
          <span className="text-[11.5px] text-ink-3" title="Mail apps cut long links — copy the text instead">Too long to open in a mail app: copy it</span>
        )}
        {email.enabled ? (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => submit(false)} disabled={Boolean(busy)} data-testid={`${testId}-log`}>
              {busy === 'log' ? <Loader2 className="animate-spin" aria-hidden="true" /> : null} I sent it myself
            </Button>
            <Button type="button" size="sm" onClick={() => submit(true)} disabled={!canSend || Boolean(busy)} data-testid={`${testId}-send`}>
              {busy === 'send' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Send aria-hidden="true" />} {busy === 'send' ? 'Sending…' : 'Send email'}
            </Button>
          </>
        ) : (
          <Button type="button" size="sm" variant={opened ? 'default' : 'outline'} className={cn(!opened && 'border-[color:var(--brand-soft-border)]')} onClick={() => submit(false)} disabled={Boolean(busy)} data-testid={`${testId}-log`}>
            {busy === 'log' ? <Loader2 className="animate-spin" aria-hidden="true" /> : null} Log as sent
          </Button>
        )}
      </div>
    </div>
  );
}

export function EmailComposer({ open, onOpenChange, title, description, initial = {}, recent, summary, onSubmit, sendHint, testId = 'email-composer' }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl border-line bg-surface text-ink">
        <DialogHeader className="text-left">
          <DialogTitle className="font-heading text-[17px] font-bold text-ink">{title}</DialogTitle>
          {description ? <DialogDescription className="text-[13px] text-ink-3">{description}</DialogDescription> : null}
        </DialogHeader>
        {open ? (
          <ComposerBody
            initial={initial}
            recent={recent}
            summary={summary}
            onSubmit={onSubmit}
            onDone={() => onOpenChange(false)}
            sendHint={sendHint}
            testId={testId}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
